import type { Rule } from '@ad-platform/tm-rule-text-viewer';
import envConfig from '@hulu/env-config';
import { useMemo } from 'react';

import {
  DROPDOWN_OPTIONS_IGNORED_DIMENSIONS,
  GeoDimension,
  PRESET_ID,
  PRESET_TARGETING_DIMENTION_RESULT,
} from '../../common/TargetingDropdowns/constants';
import type { Nullable, TargetingDefinition, TargetingDefinitionAttributeMap, TargetingValue } from '../../models';
import { toCamelCase } from '../../utils/transform/toCamelCase';
import { toKebabCase } from '../../utils/transform/toKebabCase';
import type { JSONData } from '../../utils/transform/types';
import type { TargetingRule } from '../graphql';
import type { FetchResult, UseFetchOptions } from '../hooks/useFetch';
import { useFetch } from '../hooks/useFetch';
import type {
  FetchTargetingValuesResults,
  PreDecoratedRule,
  ProviderEntryAttributeMap,
  ProviderEntryBase,
  ProviderEntryMapResponse,
  RuleFilter,
  TargetingValuesResponse,
  UiProviderEntryDimensionValuesFilter,
} from './targetingManagerApiTypes';
import { ProviderEntryFilterMatchMethodEnum } from './targetingManagerApiTypes';

/**
 * Creates a rule filter for qualifying targeting values by country and publishers.
 * Terms are only included if the corresponding data is provided.
 * @param lineItemCountryIds - Array of country targeting term values (optional)
 * @param lineItemPublishers - Array of publisher targeting values (optional)
 * @returns RuleFilter object for the API request, or undefined if no terms to include
 */
export function createRuleFilter(
  lineItemCountryIds: string[] | undefined,
  lineItemPublishers: TargetingValue[] | undefined
): RuleFilter | undefined {
  const termList: RuleFilter['definition']['termList'] = [];

  // Add publisher term if publishers are provided and non-empty
  if (lineItemPublishers && lineItemPublishers.length > 0) {
    termList.push({
      subClass: 'com.disney.digital.ads.rule.manager.common.Include',
      not: false,
      dimension: 'publisher',
      valueSet: lineItemPublishers.map((p) => p.id),
    });
  }

  // Add country term if country is provided
  if (lineItemCountryIds && lineItemCountryIds.length > 0) {
    termList.push({
      subClass: 'com.disney.digital.ads.rule.manager.common.Include',
      not: false,
      dimension: 'country',
      valueSet: lineItemCountryIds,
    });
  }

  // Return undefined if no terms to include
  if (termList.length === 0) {
    return undefined;
  }

  return {
    definition: {
      subClass: 'com.disney.digital.ads.rule.manager.common.And',
      not: false,
      termList,
    },
  };
}

const TARGETING_MANAGER_BASE_URL = envConfig.REACT_APP_MISSION_CONTROL_TM_URL;

export function useFetchDecoratedTargetingRule(rule: Nullable<TargetingRule>, preset: boolean): FetchResult<Rule> {
  const requestBody = useMemo(
    () => ({
      'targeting-rule': toKebabCase<PreDecoratedRule>(rule || {}),
    }),
    [rule]
  );

  return useFetch<Rule>(`${TARGETING_MANAGER_BASE_URL}v1/translate-rule?skip-translate-preset=${!preset}`, {
    method: 'POST',
    body: requestBody,
    enabled: !!rule && !!rule?.definition,
    refetchOnMount: true,
  });
}

export function convertToTargetingDefinitionAttributeMap({
  publisher,
  country,
  premiereDateTime,
  salesChannel,
  sportId,
  sportName,
}: ProviderEntryAttributeMap): TargetingDefinitionAttributeMap {
  return {
    publisher: publisher ?? undefined,
    country: country ?? undefined,
    premiereDateTime: premiereDateTime ?? undefined,
    salesChannel: salesChannel ?? undefined,
    sportId: sportId ?? undefined,
    sportName: sportName ?? undefined,
  };
}

/** CamelCase map values (field names) but preserve original keys (data IDs) to avoid collisions. */
function camelCaseMapValues(map: Record<string, unknown>): Record<string, ProviderEntryBase> {
  return Object.fromEntries(
    Object.entries(map).map(([key, value]) => [key, (toCamelCase(value as JSONData) as unknown) as ProviderEntryBase])
  );
}

export function convertToTargetingDefinitions(map: Record<string, ProviderEntryBase>): TargetingDefinition[] {
  return Object.values(map).map((value) => {
    const { id, primarySource, additionalSources, attributeMap } = value;
    return {
      id,
      name: primarySource.name,
      displayName: primarySource.name,
      primarySource,
      additionalSource: additionalSources || [],
      attributeMap: attributeMap ? convertToTargetingDefinitionAttributeMap(attributeMap) : null,
    };
  });
}

/**
 * Hook to fetch targeting categories
 * @param includeTargetingPresets - Whether to include targeting presets
 * @param options - Optional configuration for the fetch hook
 * @returns Categories data, loading state, and error
 */
export const useFetchTargetingCategories = (
  includeTargetingPresets: boolean = false,
  options?: UseFetchOptions<TargetingValuesResponse>
): FetchResult<TargetingValuesResponse> => {
  const queryParams = includeTargetingPresets ? '?includeTargetingPresets=true' : '';
  const fetchResult = useFetch<TargetingValuesResponse>(`${TARGETING_MANAGER_BASE_URL}v1/categories${queryParams}`, {
    method: 'GET',
    refetchOnMount: true,
    mapResponse: (responseKebab: ProviderEntryMapResponse): TargetingValuesResponse => {
      if (!responseKebab.data?.map) {
        return {
          nodes: [],
          pageInfo: { hasPreviousPage: false, hasNextPage: false },
        };
      }
      const providerEntries = camelCaseMapValues(responseKebab.data.map);
      const targetingDefinitions = convertToTargetingDefinitions(providerEntries);
      return {
        nodes: targetingDefinitions,
        pageInfo: { hasPreviousPage: !!responseKebab.links?.prev, hasNextPage: !!responseKebab.links?.next },
      };
    },
    notifyError: (message: string) => {
      console.error(`Error fetching targeting categories: ${message}`);
    },
    ...options,
  });
  return fetchResult;
};

/**
 * Hook to fetch targeting dimensions by category
 * @param categoryGuid - The category GUID to fetch dimensions for
 * @param options - Optional configuration (enabled, refetchOnMount, etc.)
 * @returns FetchResult containing targeting dimensions array
 */
export function useFetchTargetingDimensionsByCategory(
  categoryGuid: string,
  options: { enabled?: boolean; refetchOnMount?: boolean; allowEditCountryTarget?: boolean } = {}
): FetchResult<TargetingValuesResponse> {
  const { enabled = true, refetchOnMount = true, allowEditCountryTarget = false } = options;
  // Handle preset case immediately
  const isPreset = categoryGuid === PRESET_ID;
  const fetchResult = useFetch<TargetingValuesResponse>(
    `${TARGETING_MANAGER_BASE_URL}v1/categories/${categoryGuid}/dimensions?origin-source=mc`,
    {
      method: 'POST',
      body: {},
      // preset case skips invoking fetch(); invoking hook because React requires consistent hook sequence
      enabled: !isPreset && enabled,
      refetchOnMount,
      mapResponse: (responseKebab: ProviderEntryMapResponse): TargetingValuesResponse => {
        if (!responseKebab.data?.map) {
          return {
            nodes: [],
            pageInfo: { hasPreviousPage: false, hasNextPage: false },
          };
        }
        const providerEntries = camelCaseMapValues(responseKebab.data.map);
        const targetingDefinitions = convertToTargetingDefinitions(providerEntries);
        const filteredTargetingDefinitions = targetingDefinitions.filter(
          (dim) =>
            !DROPDOWN_OPTIONS_IGNORED_DIMENSIONS.includes(dim.primarySource?.id ?? '') &&
            (allowEditCountryTarget || dim.primarySource?.id !== GeoDimension.Country)
        );
        return {
          nodes: filteredTargetingDefinitions,
          pageInfo: { hasPreviousPage: !!responseKebab.links?.prev, hasNextPage: !!responseKebab.links?.next },
        };
      },
      notifyError: (message: string) => {
        console.error(`Error fetching targeting dimensions by category: ${message}`);
      },
    }
  );

  // Return predetermined preset dimension if it's a preset category
  return isPreset ? PRESET_TARGETING_DIMENTION_RESULT : fetchResult;
}

/**
 * Argument bag for `useFetchTargetingValues`.
 *
 * Lifted from a previously-positional 8-arg signature so callers (and tests)
 * can rely on TypeScript-checked names instead of positional indices. Tests
 * that previously asserted on `mockFetchHook.mock.calls[i][N]` should now
 * assert on `mockFetchHook.mock.calls[i][0].fieldName`.
 */
export type UseFetchTargetingValuesOptions = {
  /** The category GUID. */
  categoryGuid: string;
  /** The dimension ID to fetch values for. */
  dimensionId: string;
  /** Optional search term to filter values. `null` disables the value filter. */
  searchTerm: string | null;
  /** Pagination offset. */
  offset: number;
  /** Pagination limit. */
  limit: number;
  /** Country targeting values used by `createRuleFilter`. */
  lineItemCountryIds: string[] | undefined;
  /** Publisher targeting values used by `createRuleFilter`. */
  lineItemPublishers: TargetingValue[] | undefined;
  /** Toggle the underlying fetch on/off. Defaults to `true`. */
  enabled?: boolean;
  /** When true, defer the initial fetch until something else triggers it. Defaults to `false`. */
  lazy?: boolean;
  /** When true, refetch on each mount. Defaults to `true`. */
  refetchOnMount?: boolean;
  /** Optional explicit value-id allowlist (e.g. used for the Country dimension). */
  valuesFilterIds?: string[];
};

/**
 * Hook to fetch targeting values for a specific dimension with pagination support.
 *
 * Returns an enhanced FetchResult with pagination and accumulation support.
 */
export function useFetchTargetingValues(options: UseFetchTargetingValuesOptions): FetchTargetingValuesResults {
  const {
    categoryGuid,
    dimensionId,
    searchTerm,
    offset,
    limit,
    lineItemCountryIds,
    lineItemPublishers,
    enabled = true,
    lazy = false,
    refetchOnMount = true,
    valuesFilterIds,
  } = options;

  const requestBody = useMemo(() => {
    const ruleFilter = createRuleFilter(lineItemCountryIds, lineItemPublishers);
    const valuesFilter =
      valuesFilterIds?.length || searchTerm
        ? {
            ...(valuesFilterIds?.length ? { idSet: valuesFilterIds } : {}),
            ...(searchTerm && {
              matchMethod: ProviderEntryFilterMatchMethodEnum.CONTAINS,
              target: searchTerm,
            }),
          }
        : undefined;
    const filter: UiProviderEntryDimensionValuesFilter = {
      dimensionFilter: {
        idSet: dimensionId ? [dimensionId] : [],
      },
      valuesFilter,
      ruleFilter,
    };
    return toKebabCase(filter);
  }, [dimensionId, searchTerm, lineItemCountryIds, lineItemPublishers, valuesFilterIds]);

  const url = useMemo(() => {
    if (!categoryGuid) return '';
    const params = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    });
    return `${TARGETING_MANAGER_BASE_URL}v1/categories/${categoryGuid}/values?${params}`;
  }, [categoryGuid, offset, limit]);

  const fetchResult = useFetch<TargetingValuesResponse>(url, {
    method: 'POST',
    body: requestBody,
    enabled: !!categoryGuid && !!dimensionId && enabled,
    lazy,
    refetchOnMount,
    accumulate: true, // Enable accumulation
    accumulateKey: 'nodes', // Accumulate the 'nodes' array
    mapResponse: (responseKebab: ProviderEntryMapResponse): TargetingValuesResponse => {
      const providerEntries = camelCaseMapValues(responseKebab.data.map);
      return {
        nodes: convertToTargetingDefinitions(providerEntries),
        pageInfo: { hasPreviousPage: !!responseKebab.links.prev, hasNextPage: !!responseKebab.links.next },
      };
    },
    notifyError: (message: string) => {
      console.error(`Error fetching targeting values: ${message}`);
    },
  });
  const hasMore = useMemo(() => fetchResult.data?.pageInfo?.hasNextPage ?? false, [fetchResult.data]);
  return {
    data: fetchResult.data,
    loading: fetchResult.loading,
    loadingMore: fetchResult.loadingMore,
    error: fetchResult.error,
    refetch: fetchResult.refetch,
    reset: fetchResult.reset,
    hasMore,
  };
}
